<template>
  <client-only>
    <Head>
      <Title>
        {{ $t("bnpl:pageTitle.limit") }}
      </Title>
    </Head>
  </client-only>

  <div v-if="!page" class="padding16 gap12" style="margin-top: 24px">
    {{ $t("bnpl:loading") }}
  </div>
  <client-only v-else>
    <LimitWelcome v-if="page === 'login'" />
    <LimitIntro v-else-if="page === 'intro'" />
    <LimitQuestions v-else-if="page === 'data'" />
    <EmailConfirmation v-else-if="page === 'email_verification'" in-limit />
    <div v-else-if="page === 'psd2_sync'" class="pageContainer">
      <div v-if="!loading" class="flex-column grow">
        <div
          class="flex-column align-items-start padding0 gap24 stretch px-2 pt-2"
        >
          <h1 class="h bold">
            {{ $t("bnpl:limit.psd2.flow_step.name") }}
          </h1>
        </div>
        <Psd2Screens
          @done="limitStore.nextPosition()"
          @back="limitStore.previousPosition()"
        />
      </div>
    </div>
    <LimitScoring v-else-if="page === 'scoring'" />
    <LimitResult v-else-if="page === 'result'" />
  </client-only>
</template>

<script setup>
import { useSessionStore } from "~~/stores/session";
import { useLimitStore } from "~~/stores/limit";
import { LIMIT_STATUS, LIMIT_SOURCE } from "~~/utils/const";
import { useProfileStore } from "~~/stores/profile";
import { useCountryStore } from "~~/stores/country";
import { loadAllMessages } from "~/utils/i18n";
import { i18nInstance } from "~/plugins/i18n";

definePageMeta({ layout: "payment" });

const route = useRoute();

const limitStore = useLimitStore();
const sessionStore = useSessionStore();
const profileStore = useProfileStore();
const countryStore = useCountryStore();

const isLoggedIn = computed(() => sessionStore.isLoggedIn);

const page = computed(() => {
  if (!sessionStore.isLoggedIn) {
    return "login";
  }
  if (limitStore.limit?.flowPosition) {
    return limitStore.limit.flowPosition.toLowerCase();
  }
  return null;
});

useScrollTopOnChange(page);

const fetch = async () => {
  if (sessionStore.isLoggedIn) {
    loadAllMessages(i18nInstance, countryStore.locale);
    if (isEmpty(profileStore.profile)) {
      await profileStore.fetch();
    }
    try {
      await limitStore.fetchMyLimit();
      if (limitStore.limit.status === LIMIT_STATUS.CANCELLED) {
        if (LIMIT_SOURCE[limitStore.source]) {
          await limitStore.createLimit(LIMIT_SOURCE[limitStore.source]);
        } else {
          await limitStore.createLimit(LIMIT_SOURCE.PROFILE);
        }
      }
    } catch (err) {
      if (err.statusCode === 404) {
        if (LIMIT_SOURCE[limitStore.source]) {
          await limitStore.createLimit(LIMIT_SOURCE[limitStore.source]);
        } else {
          await limitStore.createLimit(LIMIT_SOURCE.PROFILE);
        }
      }
    }
  }
};

watch(
  page,
  (value) => {
    if (!value) {
      fetch();
    }
  },
  { immediate: true }
);

onBeforeMount(async () => {
  if (route.query?.source) {
    limitStore.source = route.query.source.toUpperCase();
  } else {
    limitStore.source = LIMIT_SOURCE.PROFILE;
  }
  await fetch();
  navigateTo({ name: "financier-limit", replace: true });
  Track.screenview("limit-flow");
});
</script>

<style scoped></style>
